// =============================================================================
// GoogleReviewsTab — Google-reviews op de website, zonder overtypen
// =============================================================================
// Elke nacht leest de server per vestiging de Google-score (voor de "5.0
// (1000+)" op de homepage) en alle Google-reviews. Jev, een beslismodel,
// beantwoordt per review vier vragen: positief? te citeren? over welke
// expeditie? welke taal? Boven de drempels wordt de review een item in
// Webflow; in het grijze gebied wacht hij hier in de wachtrij op een mens;
// daaronder wordt hij overgeslagen.
//
// Panelen:
//  1. Koppelingen — per vestiging het Google Place ID (score) en de Business
//     Profile-locatie (reviews), plus welke sleutels op de server staan.
//  2. Instellingen — automatisch publiceren aan/uit en de drempels.
//  3. Wachtrij — de reviews die op een beslissing wachten: publiceren (met
//     eventueel een andere expeditie) of afwijzen.
//  4. Alle reviews — filterbaar op status en vestiging.
import { useCallback, useEffect, useState } from 'react';
import {
  classifyGoogleReviews,
  decideGoogleReview,
  GOOGLE_REVIEW_STATUSES,
  getGoogleReviewsOverview,
  listGoogleReviews,
  runGoogleReviews,
  saveGoogleReviewLocation,
  saveGoogleReviewSettings,
  type GoogleReviewExpedition,
  type GoogleReviewLocation,
  type GoogleReviewRow,
  type GoogleReviewSettings,
  type GoogleReviewsError,
  type GoogleReviewsOverview,
  type GoogleReviewsRunReport,
  type GoogleReviewStatus,
} from '../api/googleReviews';

const STATUS_LABEL: Record<GoogleReviewStatus, string> = {
  new: 'Nog niet beoordeeld',
  suggested: 'Wacht op beslissing',
  approved: 'Goedgekeurd, nog niet op de site',
  published: 'Op de site',
  skipped: 'Overgeslagen',
  rejected: 'Afgewezen',
};

const SKIP_LABEL: Record<string, string> = {
  stars_below_minimum: 'te weinig sterren',
  no_text: 'geen of te korte tekst',
  not_positive: 'niet positief genoeg',
  not_publishable: 'niet geschikt om te citeren',
  not_classified: 'niet beoordeeld',
};

const LANGUAGE_LABEL: Record<string, string> = { en: 'EN', nl: 'NL', other: 'anders' };

function errText(e: GoogleReviewsError | null): string {
  if (!e) return 'Onbekende fout';
  return e.message ? `${e.error}: ${e.message}` : e.error;
}

function pct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '–';
  return `${Math.round(n * 100)}%`;
}

function stars(n: number): string {
  return '★'.repeat(Math.max(0, Math.min(5, n))) + '☆'.repeat(Math.max(0, 5 - n));
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function reviewerLabel(r: GoogleReviewRow): string {
  if (r.reviewer_anonymous) return 'Google-gebruiker';
  return r.reviewer_name?.trim() || 'Google-gebruiker';
}

// ---------------------------------------------------------------------------
export function GoogleReviewsTab() {
  const [overview, setOverview] = useState<GoogleReviewsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    const res = await getGoogleReviewsOverview();
    if (res.ok && res.data) setOverview(res.data);
    else setError(errText(res.error));
    setLoading(false);
  }, []);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load, refreshKey]);

  const reload = () => setRefreshKey((k) => k + 1);

  return (
    <div className="marketing-followup">
      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <p className="muted">Laden…</p> : null}

      {overview ? (
        <>
          <ActionsPanel overview={overview} onDone={reload} onError={setError} />
          <LinksPanel overview={overview} onSaved={(locations) => setOverview({ ...overview, locations })} onError={setError} />
          <SettingsPanel
            settings={overview.settings}
            onSaved={(settings) => setOverview({ ...overview, settings })}
            onError={setError}
          />
          <QueuePanel
            key={`queue-${refreshKey}`}
            locations={overview.locations}
            expeditions={overview.expeditions}
            webflowConfigured={overview.configured.webflow}
            onChanged={reload}
            onError={setError}
          />
          <AllReviewsPanel
            key={`all-${refreshKey}`}
            locations={overview.locations}
            expeditions={overview.expeditions}
            onChanged={reload}
            onError={setError}
          />
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Acties: nu draaien, droogloop, nieuwe reviews beoordelen
// ---------------------------------------------------------------------------
function ActionsPanel({
  overview,
  onDone,
  onError,
}: {
  overview: GoogleReviewsOverview;
  onDone: () => void;
  onError: (e: string | null) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [report, setReport] = useState<GoogleReviewsRunReport | null>(null);
  const [classifyNote, setClassifyNote] = useState<string | null>(null);
  const c = overview.configured;

  const run = async (dry: boolean) => {
    setBusy(dry ? 'dry' : 'run');
    onError(null);
    setReport(null);
    const res = await runGoogleReviews(dry);
    setBusy(null);
    if (res.ok && res.data) {
      setReport(res.data);
      if (!dry) onDone();
    } else onError(errText(res.error));
  };

  const classify = async () => {
    setBusy('classify');
    onError(null);
    setClassifyNote(null);
    const res = await classifyGoogleReviews();
    setBusy(null);
    if (res.ok && res.data) {
      const r = res.data.classify;
      setClassifyNote(
        `${r.considered} bekeken, ${r.jev_calls} aan Jev gevraagd: ${r.suggested} naar de wachtrij, ${r.approved} goedgekeurd, ${r.skipped} overgeslagen, ${r.failed} mislukt.`,
      );
      onDone();
    } else onError(errText(res.error));
  };

  const chip = (ok: boolean, label: string) => (
    <span className="muted small" style={{ marginRight: 12 }}>
      {ok ? '✓' : '✗'} {label}
    </span>
  );

  return (
    <section className="marketing-panel">
      <h2>Google-reviews</h2>
      <p className="muted marketing-hint">
        Elke nacht om 06:00 leest de server de Google-score en de reviews van elke vestiging, laat
        Jev ze beoordelen en zet de goede in Webflow. Hier kun je dat nu meteen laten doen.
        {overview.queue_count > 0 ? ` Er wachten ${overview.queue_count} reviews op een beslissing.` : ''}
      </p>
      <div>
        {chip(c.places, 'Google Places (score)')}
        {chip(c.business_profile, 'Business Profile (reviews)')}
        {chip(c.jev, 'Jev (beoordeling)')}
        {chip(c.webflow, 'Webflow (publiceren)')}
      </div>
      {overview.webflow_error ? (
        <p className="muted small">Webflow gaf een fout bij het lezen van de expedities: {overview.webflow_error}</p>
      ) : null}
      <div className="marketing-actions">
        <button className="walkin-primary-btn" disabled={busy !== null} onClick={() => void run(false)}>
          {busy === 'run' ? 'Bezig…' : 'Nu ophalen en beoordelen'}
        </button>
        <button className="btn-link" disabled={busy !== null} onClick={() => void run(true)}>
          {busy === 'dry' ? 'Bezig…' : 'Droogloop (niets opslaan)'}
        </button>
        <button className="btn-link" disabled={busy !== null || !c.jev} onClick={() => void classify()}>
          {busy === 'classify' ? 'Bezig…' : 'Nog niet beoordeelde reviews aan Jev voorleggen'}
        </button>
      </div>
      {classifyNote ? <p className="muted small">{classifyNote}</p> : null}
      {report ? <RunReport report={report} /> : null}
    </section>
  );
}

function RunReport({ report }: { report: GoogleReviewsRunReport }) {
  const inserted = report.imports.reduce((n, i) => n + i.inserted, 0);
  const updated = report.imports.reduce((n, i) => n + i.updated, 0);
  return (
    <div className="muted small">
      <p>
        {report.dry_run ? 'Droogloop' : 'Uitgevoerd'} in {Math.round(report.took_ms / 100) / 10} s:
        {' '}{inserted} nieuwe review(s), {updated} gewijzigd
        {report.classify ? `, ${report.classify.jev_calls} keer Jev gevraagd (${report.classify.suggested} wachtrij, ${report.classify.approved} goedgekeurd, ${report.classify.skipped} overgeslagen)` : ''}
        {report.publish ? `, ${report.publish.published} op de site gezet${report.publish.failed ? ` (${report.publish.failed} mislukt)` : ''}` : ''}
        {report.rating_items.length ? `, score-items ${report.rating_items.filter((r) => r.published).length}/${report.rating_items.length} ververst` : ''}.
      </p>
      {report.ratings.map((r) => (
        <p key={r.location}>
          Score {r.location}: {r.error ? `fout: ${r.error}` : `${r.rating ?? '–'} (${r.review_count ?? '–'} reviews, via ${r.source})`}
        </p>
      ))}
      {report.imports.filter((i) => i.error).map((i) => (
        <p key={i.location}>Reviews {i.location}: fout: {i.error}</p>
      ))}
      {report.errors.map((e, i) => <p key={i}>Fout: {e}</p>)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Koppelingen
// ---------------------------------------------------------------------------
function LinksPanel({
  overview,
  onSaved,
  onError,
}: {
  overview: GoogleReviewsOverview;
  onSaved: (locations: GoogleReviewLocation[]) => void;
  onError: (e: string | null) => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, { place: string; business: string }>>(() =>
    Object.fromEntries(
      overview.locations.map((l) => [
        l.id,
        { place: l.google_place_id ?? '', business: l.google_business_location ?? '' },
      ]),
    ),
  );
  const [saving, setSaving] = useState<string | null>(null);

  const save = async (loc: GoogleReviewLocation) => {
    const d = drafts[loc.id] ?? { place: '', business: '' };
    setSaving(loc.id);
    onError(null);
    const res = await saveGoogleReviewLocation({
      location_id: loc.id,
      google_place_id: d.place.trim(),
      google_business_location: d.business.trim(),
    });
    setSaving(null);
    if (res.ok && res.data) onSaved(res.data.locations);
    else onError(errText(res.error));
  };

  return (
    <section className="marketing-panel">
      <h2>Koppelingen per vestiging</h2>
      <p className="muted marketing-hint">
        Het Place ID vind je met de Place ID Finder van Google Maps Platform; de Business
        Profile-locatie is de naam <code>accounts/…/locations/…</code> uit de Business Profile API.
        Zonder Place ID geen score op de site; zonder Business Profile-locatie geen reviews.
      </p>
      <table className="vouchers-table marketing-table">
        <thead>
          <tr>
            <th>Vestiging</th>
            <th>Place ID</th>
            <th>Business Profile-locatie</th>
            <th>Google-score</th>
            <th>Reviews</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {overview.locations.map((l) => {
            const d = drafts[l.id] ?? { place: '', business: '' };
            const dirty = d.place.trim() !== (l.google_place_id ?? '') ||
              d.business.trim() !== (l.google_business_location ?? '');
            const c = l.counts;
            return (
              <tr key={l.id}>
                <td>{l.display_name}</td>
                <td>
                  <input
                    type="text"
                    value={d.place}
                    placeholder="ChIJ…"
                    onChange={(e) => setDrafts({ ...drafts, [l.id]: { ...d, place: e.target.value } })}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={d.business}
                    placeholder="accounts/123/locations/456"
                    onChange={(e) => setDrafts({ ...drafts, [l.id]: { ...d, business: e.target.value } })}
                  />
                </td>
                <td>
                  {l.rating
                    ? (
                      <span>
                        {l.rating.rating ?? '–'} · {l.rating.review_count ?? '–'} reviews
                        <br />
                        <span className="muted small">{fmtDateTime(l.rating.fetched_at)}</span>
                      </span>
                    )
                    : <span className="muted">nog niet gelezen</span>}
                </td>
                <td className="muted small">
                  {c.published ?? 0} op de site · {c.suggested ?? 0} wachten · {c.new ?? 0} nieuw
                  {c.skipped ? ` · ${c.skipped} overgeslagen` : ''}
                </td>
                <td>
                  <button className="btn-link" disabled={!dirty || saving === l.id} onClick={() => void save(l)}>
                    {saving === l.id ? 'Opslaan…' : 'Opslaan'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 2. Instellingen
// ---------------------------------------------------------------------------
function SettingsPanel({
  settings,
  onSaved,
  onError,
}: {
  settings: GoogleReviewSettings;
  onSaved: (s: GoogleReviewSettings) => void;
  onError: (e: string | null) => void;
}) {
  const [draft, setDraft] = useState<GoogleReviewSettings>(settings);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  const save = async () => {
    setSaving(true);
    onError(null);
    const res = await saveGoogleReviewSettings(draft);
    setSaving(false);
    if (res.ok && res.data) {
      onSaved(res.data.settings);
      setDraft(res.data.settings);
    } else onError(errText(res.error));
  };

  const pctField = (
    key: 'min_positive' | 'min_publishable' | 'suggest_floor' | 'min_expedition_confidence',
    label: string,
    hint: string,
  ) => (
    <label className="walkin-field">
      <span>{label}</span>
      <input
        type="number"
        min={0}
        max={100}
        step={5}
        value={Math.round(draft[key] * 100)}
        onChange={(e) => setDraft({ ...draft, [key]: Math.max(0, Math.min(100, Number(e.target.value) || 0)) / 100 })}
      />
      <small className="muted">{hint}</small>
    </label>
  );

  return (
    <section className="marketing-panel">
      <h2>Instellingen</h2>
      <div className="walkin-row">
        <label className="walkin-field">
          <span>
            <input
              type="checkbox"
              checked={draft.auto_publish}
              onChange={(e) => setDraft({ ...draft, auto_publish: e.target.checked })}
            />{' '}
            Automatisch publiceren
          </span>
          <small className="muted">
            Uit: alles wat door de drempels komt wacht hieronder op jouw ja. Aan: het staat de
            volgende ochtend op de site. Zet dit pas aan als de wachtrij een paar weken klopt.
          </small>
        </label>
        <label className="walkin-field">
          <span>Minimaal aantal sterren</span>
          <select value={draft.min_stars} onChange={(e) => setDraft({ ...draft, min_stars: Number(e.target.value) })}>
            {[5, 4, 3].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <small className="muted">Reviews met minder sterren worden niet eens aan Jev voorgelegd.</small>
        </label>
        <label className="walkin-field">
          <span>Minimale lengte</span>
          <input
            type="number"
            min={0}
            max={1000}
            value={draft.min_comment_chars}
            onChange={(e) => setDraft({ ...draft, min_comment_chars: Math.max(0, Number(e.target.value) || 0) })}
          />
          <small className="muted">tekens; kortere teksten zeggen niets op de site</small>
        </label>
      </div>
      <div className="walkin-row">
        {pctField('min_positive', 'Positief, minimaal', '% zekerheid van Jev dat de review positief is, om te publiceren')}
        {pctField('min_publishable', 'Te citeren, minimaal', '% zekerheid dat de tekst zo op de site kan (geen namen van medewerkers, geen incident)')}
        {pctField('suggest_floor', 'Wachtrij vanaf', '% op allebei; daaronder wordt de review overgeslagen, daarboven komt hij in de wachtrij')}
        {pctField('min_expedition_confidence', 'Expeditie, minimaal', '% zekerheid voordat de review aan een expeditie wordt gekoppeld')}
      </div>
      <div className="walkin-row">
        <label className="walkin-field">
          <span>Per nacht beoordelen</span>
          <input
            type="number"
            min={1}
            max={2000}
            value={draft.classify_per_tick}
            onChange={(e) => setDraft({ ...draft, classify_per_tick: Math.max(1, Number(e.target.value) || 1) })}
          />
          <small className="muted">reviews per nacht aan Jev voorleggen (de achterstand van oude reviews)</small>
        </label>
      </div>
      <div className="marketing-actions">
        <button className="walkin-primary-btn" disabled={!dirty || saving} onClick={() => void save()}>
          {saving ? 'Opslaan…' : 'Instellingen opslaan'}
        </button>
        {dirty ? <button className="btn-link" onClick={() => setDraft(settings)}>Herstel</button> : null}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 3. Wachtrij
// ---------------------------------------------------------------------------
function QueuePanel({
  locations,
  expeditions,
  webflowConfigured,
  onChanged,
  onError,
}: {
  locations: GoogleReviewLocation[];
  expeditions: GoogleReviewExpedition[];
  webflowConfigured: boolean;
  onChanged: () => void;
  onError: (e: string | null) => void;
}) {
  const [rows, setRows] = useState<GoogleReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await listGoogleReviews({ status: 'suggested', limit: 50 });
      if (cancelled) return;
      if (res.ok && res.data) {
        setRows(res.data.reviews);
        setTotal(res.data.total);
      } else onError(errText(res.error));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [onError]);

  return (
    <section className="marketing-panel">
      <h2>Wachtrij{total ? ` (${total})` : ''}</h2>
      <p className="muted marketing-hint">
        Reviews die Jev goed genoeg vond om aan jou voor te leggen. Publiceren zet ze meteen in
        Webflow{webflowConfigured ? '' : ' (zodra de Webflow-koppeling op de server staat)'}; de
        expeditie kun je nog aanpassen. Afwijzen is definitief, maar de review blijft hieronder terug
        te vinden.
      </p>
      {loading ? <p className="muted">Laden…</p> : null}
      {!loading && rows.length === 0 ? <p className="muted">Niets te beslissen.</p> : null}
      {rows.map((r) => (
        <ReviewCard
          key={r.review_id}
          row={r}
          locations={locations}
          expeditions={expeditions}
          onChanged={onChanged}
          onError={onError}
        />
      ))}
    </section>
  );
}

function ReviewCard({
  row,
  locations,
  expeditions,
  onChanged,
  onError,
}: {
  row: GoogleReviewRow;
  locations: GoogleReviewLocation[];
  expeditions: GoogleReviewExpedition[];
  onChanged: () => void;
  onError: (e: string | null) => void;
}) {
  const [expedition, setExpedition] = useState<string>(row.published_expedition_key ?? row.expedition_key ?? '');
  const [busy, setBusy] = useState<'publish' | 'reject' | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const venue = locations.find((l) => l.id === row.location_id)?.display_name ?? '–';
  const jevPick = row.expedition_key
    ? `${expeditions.find((e) => e.key === row.expedition_key)?.name ?? row.expedition_key} (${pct(row.expedition_confidence)})`
    : `geen expeditie (${pct(row.expedition_confidence)})`;

  const act = async (decision: 'publish' | 'reject') => {
    setBusy(decision);
    onError(null);
    setNote(null);
    const res = await decideGoogleReview(row.review_id, decision, decision === 'publish' ? expedition : undefined);
    setBusy(null);
    if (res.ok && res.data) {
      const failed = res.data.publish?.items.find((i) => i.error);
      if (failed) setNote(`Webflow gaf een fout: ${failed.error}`);
      else if (decision === 'publish' && !res.data.webflow_configured) {
        setNote('Goedgekeurd; komt op de site zodra de Webflow-koppeling er is.');
      }
      onChanged();
    } else onError(errText(res.error));
  };

  return (
    <div className="marketing-row" style={{ borderTop: '1px solid var(--border, #ddd)', padding: '12px 0' }}>
      <div className="muted small">
        {stars(row.star_rating)} · {fmtDate(row.google_created_at)} · {venue} · {reviewerLabel(row)}
        {row.language ? ` · ${LANGUAGE_LABEL[row.language] ?? row.language}` : ''}
      </div>
      <p style={{ whiteSpace: 'pre-wrap', margin: '6px 0' }}>{row.comment}</p>
      <div className="muted small">
        Jev: positief {pct(row.positive)} · te citeren {pct(row.publishable)} · expeditie {jevPick}
      </div>
      <div className="walkin-row" style={{ alignItems: 'end' }}>
        <label className="walkin-field">
          <span>Expeditie op de site</span>
          <select value={expedition} onChange={(e) => setExpedition(e.target.value)}>
            <option value="">Geen (alleen homepage)</option>
            {expeditions.map((e) => <option key={e.key} value={e.key}>{e.name}</option>)}
          </select>
        </label>
        <div className="marketing-actions">
          <button className="walkin-primary-btn" disabled={busy !== null} onClick={() => void act('publish')}>
            {busy === 'publish' ? 'Bezig…' : 'Publiceren'}
          </button>
          <button className="btn-link voucher-deactivate" disabled={busy !== null} onClick={() => void act('reject')}>
            {busy === 'reject' ? 'Bezig…' : 'Afwijzen'}
          </button>
        </div>
      </div>
      {note ? <p className="muted small">{note}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. Alle reviews
// ---------------------------------------------------------------------------
function AllReviewsPanel({
  locations,
  expeditions,
  onChanged,
  onError,
}: {
  locations: GoogleReviewLocation[];
  expeditions: GoogleReviewExpedition[];
  onChanged: () => void;
  onError: (e: string | null) => void;
}) {
  const [status, setStatus] = useState<GoogleReviewStatus | ''>('published');
  const [locationId, setLocationId] = useState('');
  const [rows, setRows] = useState<GoogleReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const limit = 50;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const res = await listGoogleReviews({
        status: status || null,
        location_id: locationId || null,
        limit,
        offset,
      });
      if (cancelled) return;
      if (res.ok && res.data) {
        setRows(res.data.reviews);
        setTotal(res.data.total);
      } else onError(errText(res.error));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [status, locationId, offset, onError]);

  const publishAnyway = async (r: GoogleReviewRow) => {
    setBusy(r.review_id);
    onError(null);
    const res = await decideGoogleReview(r.review_id, 'publish');
    setBusy(null);
    if (res.ok) onChanged();
    else onError(errText(res.error));
  };

  const classifyOne = async (r: GoogleReviewRow) => {
    setBusy(r.review_id);
    onError(null);
    const res = await classifyGoogleReviews(r.review_id);
    setBusy(null);
    if (res.ok) onChanged();
    else onError(errText(res.error));
  };

  const expeditionName = (key: string | null) =>
    key ? expeditions.find((e) => e.key === key)?.name ?? key : '–';

  return (
    <section className="marketing-panel">
      <h2>Alle reviews</h2>
      <div className="walkin-row">
        <label className="walkin-field">
          <span>Status</span>
          <select
            value={status}
            onChange={(e) => {
              setOffset(0);
              setStatus(e.target.value as GoogleReviewStatus | '');
            }}
          >
            <option value="">Alles</option>
            {GOOGLE_REVIEW_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </label>
        <label className="walkin-field">
          <span>Vestiging</span>
          <select
            value={locationId}
            onChange={(e) => {
              setOffset(0);
              setLocationId(e.target.value);
            }}
          >
            <option value="">Alle vestigingen</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.display_name}</option>)}
          </select>
        </label>
      </div>
      {loading ? <p className="muted">Laden…</p> : null}
      {!loading && rows.length === 0 ? <p className="muted">Geen reviews.</p> : null}
      {rows.length > 0 ? (
        <table className="vouchers-table marketing-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Vestiging</th>
              <th>Sterren</th>
              <th>Review</th>
              <th>Jev</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.review_id}>
                <td className="muted small">{fmtDate(r.google_created_at)}</td>
                <td className="muted small">{locations.find((l) => l.id === r.location_id)?.display_name ?? '–'}</td>
                <td>{stars(r.star_rating)}</td>
                <td>
                  <span className="muted small">{reviewerLabel(r)}</span>
                  <br />
                  {(r.comment ?? '').length > 220 ? `${(r.comment ?? '').slice(0, 220)}…` : r.comment ?? <span className="muted">(geen tekst)</span>}
                </td>
                <td className="muted small">
                  {r.classified_at
                    ? `positief ${pct(r.positive)} · te citeren ${pct(r.publishable)} · ${expeditionName(r.expedition_key)}`
                    : '–'}
                </td>
                <td className="muted small">
                  {STATUS_LABEL[r.status]}
                  {r.status === 'skipped' && r.skip_reason ? ` (${SKIP_LABEL[r.skip_reason] ?? r.skip_reason})` : ''}
                  {r.status === 'published' ? ` · ${expeditionName(r.published_expedition_key)}` : ''}
                  {r.error ? <><br />fout: {r.error}</> : null}
                </td>
                <td>
                  {r.status === 'new' ? (
                    <button className="btn-link" disabled={busy === r.review_id} onClick={() => void classifyOne(r)}>
                      Beoordelen
                    </button>
                  ) : null}
                  {r.status === 'skipped' || r.status === 'rejected' || r.status === 'approved' ? (
                    <button className="btn-link" disabled={busy === r.review_id} onClick={() => void publishAnyway(r)}>
                      {r.status === 'approved' ? 'Opnieuw publiceren' : 'Toch publiceren'}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {total > limit ? (
        <div className="marketing-actions">
          <button className="btn-link" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
            Vorige
          </button>
          <span className="muted small">
            {offset + 1}–{Math.min(offset + limit, total)} van {total}
          </span>
          <button className="btn-link" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>
            Volgende
          </button>
        </div>
      ) : null}
    </section>
  );
}
